import { computed, reactive, ref } from 'vue'
import { distinct, nonNull, sum } from '../../shared/fns'
import { getTestCaseOrder } from '../../shared/test-case-order'
import { testCaseStatuses } from '../test-case/test-case-statuses'
import { selectedTestCases } from '../setup/setup-logic'

const testCaseStatusOrder = code => testCaseStatuses[code]?.sortOrder

export const compareTestCases = (a, b) => {
  if (testCaseStatusOrder(a.status) < testCaseStatusOrder(b.status)) {
    return -1
  }
  if (testCaseStatusOrder(a.status) > testCaseStatusOrder(b.status)) {
    return 1
  }

  if (a.outcome === false && b.outcome !== false) {
    return -1
  }
  if (a.outcome !== false && b.outcome === false) {
    return 1
  }

  const aOrder = getTestCaseOrder(a)
  const bOrder = getTestCaseOrder(b)

  if (aOrder.code != null && bOrder.code != null) {
    return Math.sign(aOrder.code - bOrder.code)
  }
  if (aOrder.code != null && bOrder.code == null) {
    return -1
  }
  if (aOrder.code == null && bOrder.code != null) {
    return 1
  }

  if (aOrder.category != null && bOrder.category != null) {
    return Math.sign(aOrder.category - bOrder.category)
  }
  if (aOrder.category != null && bOrder.category == null) {
    return -1
  }
  if (aOrder.category == null && bOrder.category != null) {
    return 1
  }

  if (aOrder.group != null && bOrder.group != null) {
    return Math.sign(aOrder.group - bOrder.group)
  }
  if (aOrder.group != null && bOrder.group == null) {
    return -1
  }
  if (aOrder.group == null && bOrder.group != null) {
    return 1
  }

  if (a.category < b.category) {
    return -1
  }
  if (a.category > b.category) {
    return 1
  }

  const aGroups = a.groups?.join(' ')
  const bGroups = b.groups?.join(' ')
  if (aGroups < bGroups) {
    return -1
  }
  if (aGroups > bGroups) {
    return 1
  }

  if (a.code < b.code) {
    return -1
  }
  if (a.code > b.code) {
    return 1
  }

  return 0
}

const sortedSelectedTestCases = computed(() =>
  [...selectedTestCases.value].sort(compareTestCases)
)

export const testCases = sortedSelectedTestCases

export const codes = computed(() =>
  selectedTestCases.value
    .map(({ code }) => code)
    .filter(distinct)
)

export const categories = computed(() =>
  selectedTestCases.value
    .map(({ category }) => category)
    .filter(distinct)
)

export const groups = computed(() =>
  selectedTestCases.value
    .flatMap(({ groups }) => groups)
    .filter(distinct)
)

export const testCaseSearch = reactive({})

export const searchedTestCases = computed(() =>
  testCases.value
    .filter(({ category, groups, code, name }) =>
      (!testCaseSearch.category || testCaseSearch.category === category) &&
      (!testCaseSearch.group || groups.includes(testCaseSearch.group)) &&
      (!testCaseSearch.testCase ||
        code.toUpperCase().indexOf(testCaseSearch.testCase.toUpperCase()) >= 0 ||
        name.toUpperCase().indexOf(testCaseSearch.testCase.toUpperCase()) >= 0)
    )
)

export const testCaseCode = ref()

export const testCase = computed(() =>
  testCases.value
    .find(({ code }) => code === testCaseCode.value)
)

const testCaseIndex = computed(() =>
  testCases.value
    .findIndex(({ code }) => code === testCaseCode.value)
)

export const previousTestCase = computed(() =>
  testCaseIndex.value > 0 ? testCases.value[testCaseIndex.value - 1] : null
)

export const nextTestCase = computed(() =>
  testCaseIndex.value < testCases.value.length ? testCases.value[testCaseIndex.value + 1] : null
)

export const totalWorkDuration = computed(() =>
  sum(
    ...testCases.value
      .map(({ workDuration }) => workDuration)
      .filter(nonNull)
  )
)

export const totalStepCount = computed(() =>
  sum(
    ...testCases.value
      .map(({ steps }) => steps.length)
  )
)

export const finishedStepCount = computed(() =>
  sum(
    ...testCases.value
      .filter(({ status }) => status === 'finished')
      .map(({ steps }) => steps.length)
  )
)
